import { Subject } from '../services/subject';

/**
 * 获取科目的完整路径名称
 * @param subject 科目对象
 * @param subjects 所有科目列表
 * @returns 完整路径名称，如"一级/二级/三级"
 */
export function getFullSubjectPath(subject: Subject, subjects: Subject[]): string {
  const path: string[] = [subject.name];
  
  // 递归查找父级科目
  let currentParentId = subject.parentId;
  const findParents = (): void => {
    if (!currentParentId) return;
    const parent = subjects.find(s => s.id === currentParentId);
    if (parent) {
      path.unshift(parent.name);
      currentParentId = parent.parentId;
      findParents();
    }
  };
  
  findParents();
  return path.join(' > ');
}

/**
 * 生成科目的完整显示名称：父父级/父级/自己 - 编号（code）
 * @param subject 科目对象
 * @param subjects 所有科目列表（可选，如果不提供则只显示考核科目和编号）
 * @param separator 层级分隔符，默认为 '/'
 * @returns 完整显示名称，如"父父级/父级/自己 - 编号"
 */
export function getSubjectDisplayName(
  subject: Subject, 
  subjects?: Subject[], 
  separator: string = '/'
): string {
  if (!subjects) {
    // 如果没有提供科目列表，只返回考核科目和编号
    const code = subject.code;
    const codeString = code ? ` - ${code}` : '';
    return `${subject.name}${codeString}`;
  }
  
  // 构建层级路径
  const buildPath = (currentSubject: Subject): string[] => {
    const path = [currentSubject.name];
    
    if (currentSubject.parentId) {
      const parent = subjects.find(item => item.id === currentSubject.parentId);
      if (parent) {
        path.unshift(...buildPath(parent));
      }
    }
    
    return path;
  };
  
  const pathArray = buildPath(subject);
  const pathString = pathArray.join(separator);
  
  // 添加编号（如果存在）
  const code = subject.code;
  const codeString = code ? ` - ${code}` : '';
  
  return `${pathString}${codeString}`;
}

/**
 * 查找科目的所有祖先科目
 * @param subject 科目对象
 * @param subjects 所有科目列表
 * @returns 祖先科目列表（按层级排序，从高到低）
 */
export function findAncestorSubjects(subject: Subject, subjects: Subject[]): Subject[] {
  const ancestors: Subject[] = [];
  
  let currentParentId = subject.parentId;
  const findParents = (): void => {
    if (!currentParentId) return;
    const parent = subjects.find(s => s.id === currentParentId);
    if (parent) {
      ancestors.unshift(parent);
      currentParentId = parent.parentId;
      findParents();
    }
  };
  
  findParents();
  return ancestors;
}

/**
 * 计算科目的层级深度
 * @param subject 科目对象
 * @param subjects 所有科目列表
 * @returns 层级深度（1表示顶级科目）
 */
export function getSubjectLevel(subject: Subject, subjects: Subject[]): number {
  if (!subject.parentId) return 1;
  
  const parent = subjects.find(item => item.id === subject.parentId);
  return parent ? getSubjectLevel(parent, subjects) + 1 : 1;
}

/**
 * 扩展科目对象，添加父级和祖父级名称
 * @param subjects 原始科目列表
 * @returns 扩展后的科目列表
 */
export function extendSubjectsWithAncestors(subjects: Subject[]): (Subject & {
  parentName?: string;
  grandParentName?: string;
})[] {
  return subjects.map(subject => {
    const ancestors = findAncestorSubjects(subject, subjects);
    return {
      ...subject,
      parentName: ancestors.length > 0 ? ancestors[ancestors.length - 1].name : undefined,
      grandParentName: ancestors.length > 1 ? ancestors[ancestors.length - 2].name : undefined
    };
  });
}

/**
 * 将科目列表转换为树形结构
 * @param subjects 科目列表
 * @returns 树形结构的科目列表
 */
export function transformSubjectsToTree(subjects: Subject[]): (Subject & { children?: Subject[] })[] {
  const itemMap = new Map<string, Subject & { children?: Subject[] }>();
  
  // 首先创建一个映射
  subjects.forEach(item => {
    itemMap.set(item.id, { 
      ...item, 
      children: []
    });
  });
  
  const result: (Subject & { children?: Subject[] })[] = [];
  
  // 构建树形结构
  subjects.forEach(item => {
    const currentItem = itemMap.get(item.id);
    if (item.parentId && itemMap.has(item.parentId)) {
      const parent = itemMap.get(item.parentId);
      parent?.children?.push(currentItem!);
    } else {
      result.push(currentItem!);
    }
  });

  // 移除空的 children 数组
  const cleanEmptyChildren = (items: (Subject & { children?: Subject[] })[]):
    (Subject & { children?: Subject[] })[] => items.map(item => {
    if (item.children?.length === 0) {
      const { children, ...rest } = item;
      return rest;
    }
    return {
      ...item,
      children: item.children && item.children.length > 0 
        ? cleanEmptyChildren(item.children) 
        : undefined
    };
  });

  return cleanEmptyChildren(result);
} 